import { FC, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { Modal } from "components/modals/Modal"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { CMS_LOCALE_NAMES, CMS_LOCALES, CmsPageInput, CmsPageType } from "services/cms-seo/type"

import { slugify } from "./helpers/format"
import { useBrandSite } from "./BrandSiteContext"
import { Field, FieldRow, ghostButtonClass, inputClass, modalBoxClass, primaryButtonClass} from "./CmsUi"

interface CreatePageModalProps {
  open: boolean
  defaultType: CmsPageType
  defaultLocale: string
  onClose: () => void
  onCreated: (id: string) => void
}

export const CreatePageModal: FC<CreatePageModalProps> = ({ open, defaultType, defaultLocale, onClose, onCreated }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [locale, setLocale] = useState(defaultLocale)
  const [type, setType] = useState<CmsPageType>(defaultType)
  const [title, setTitle] = useState("")
  const [slug, setSlug] = useState("")
  const [slugTouched, setSlugTouched] = useState(false)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!open) return
    setLocale(defaultLocale)
    setType(defaultType)
    setTitle("")
    setSlug("")
    setSlugTouched(false)
  }, [open, defaultLocale, defaultType])

  const effectiveSlug = slugTouched ? slug : slugify(title)

  const submit = async () => {
    if (!title.trim() || !effectiveSlug) {
      toast.error(t("cmsSeo.createMissing", { defaultValue: "A page needs a title and a slug" }) as string)
      return
    }

    const draft: Partial<CmsPageInput> = {
      locale,
      type,
      slug: effectiveSlug,
      title: title.trim(),
      h1: title.trim(),
      seo_title: title.trim(),
      status: "draft"
    }

    setSaving(true)
    const created = await cmsSeoService.createPage(draft)
    setSaving(false)
    if (created) {
      toast.success(t("cmsSeo.pageCreated", { defaultValue: "Draft created" }) as string)
      onCreated(created.id)
    }
  }

  return (
    <Modal open={open} handler={onClose}>
      <div className={`${modalBoxClass} max-w-xl`}>
        <h3 className="mb-4 text-lg font-semibold">{t("cmsSeo.createPage", { defaultValue: "+ Create page" })}</h3>

        <FieldRow>
          <Field label={t("cmsSeo.filter.locale", { defaultValue: "Locale" })}>
            <select className={inputClass} value={locale} onChange={(event) => setLocale(event.target.value)}>
              {CMS_LOCALES.map((code) => (
                <option key={code} value={code}>
                  {CMS_LOCALE_NAMES[code]} ({code.toUpperCase()})
                </option>
              ))}
            </select>
          </Field>
          <Field label={t("cmsSeo.filter.type", { defaultValue: "Type" })}>
            <select className={inputClass} value={type} onChange={(event) => setType(event.target.value as CmsPageType)}>
              <option value="page">{t("cmsSeo.type.page", { defaultValue: "Page" })}</option>
              <option value="article">{t("cmsSeo.type.article", { defaultValue: "Article" })}</option>
              <option value="landing">{t("cmsSeo.type.landing", { defaultValue: "Landing" })}</option>
            </select>
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label={t("cmsSeo.field.title", { defaultValue: "Page title" })}>
            <input className={inputClass} value={title} onChange={(event) => setTitle(event.target.value)} autoFocus />
          </Field>
        </FieldRow>

        <FieldRow>
          <Field label={t("cmsSeo.field.slug", { defaultValue: "Slug" })} hint={`${site.host}${site.path(locale, `/${effectiveSlug || "…"}`)}`}>
            <input
              className={`${inputClass} font-mono`}
              value={effectiveSlug}
              onChange={(event) => {
                setSlugTouched(true)
                setSlug(event.target.value)
              }}
              onBlur={(event) => setSlug(slugify(event.target.value))}
            />
          </Field>
        </FieldRow>

        <div className="modal-action flex-wrap">
          <button type="button" className={ghostButtonClass} onClick={onClose}>
            {t("common.cancel", { defaultValue: "Cancel" })}
          </button>
          <button type="button" className={primaryButtonClass} disabled={saving} onClick={submit}>
            {t("common.create", { defaultValue: "Create" })}
          </button>
        </div>
      </div>
    </Modal>
  )
}
